import * as Slot from "@radix-ui/react-slot";
import type { ButtonHTMLAttributes, PropsWithChildren, Ref } from "react";

import { cn } from "../../lib/utils";

type ButtonProps = PropsWithChildren<
  ButtonHTMLAttributes<HTMLButtonElement> & {
    asChild?: boolean;
    /** React 19 forwards `ref` as an ordinary prop; typed so a caller can
     * focus a CTA (the bank-link fallback button). */
    ref?: Ref<HTMLButtonElement>;
    loading?: boolean;
    /**
     * `pill` is the onboarding funnel's action shape (every CTA there is a
     * full pill, sized to sit on one baseline with its 48px inputs). The
     * radius utilities are mutually exclusive by construction — `cn` does no
     * Tailwind merging, so a pill must never ALSO carry `rounded-2xl`.
     */
    shape?: "default" | "pill";
    size?: "sm" | "default" | "lg";
    variant?:
      | "default"
      | "primary-dark"
      | "secondary"
      | "outline"
      | "ghost"
      | "destructive";
  }
>;

export function Button({
  asChild = false,
  children,
  className,
  loading = false,
  shape = "default",
  size = "default",
  type = "button",
  variant = "default",
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";
  const content =
    loading && !asChild ? (
      <>
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
        {children}
      </>
    ) : (
      children
    );

  return (
    <Comp
      type={type}
      className={cn(
        "inline-flex min-h-11 items-center justify-center gap-2 border px-4 text-sm font-semibold transition-[color,background-color,border-color,box-shadow,opacity,scale] duration-200 ease-[cubic-bezier(0.2,0,0.13,1)] active:scale-[0.96] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100",
        // Exactly one radius utility per render (see `shape`).
        shape === "pill"
          ? "rounded-pill"
          : size === "sm"
            ? "rounded-xl"
            : "rounded-2xl",
        // Exactly one border color per variant: `cn` does no Tailwind merging,
        // so a base `border-transparent` beside the outline variant's
        // `border-border` resolves by stylesheet order — and transparent wins.
        variant === "outline" ? "border-border" : "border-transparent",
        size === "sm" && "min-h-9 px-3 text-xs",
        size === "default" && "min-h-11 px-4 text-sm",
        size === "lg" && "min-h-14 px-6 text-base",
        variant === "default" &&
          "bg-primary text-primary-foreground shadow-sm hover:bg-primary/92",
        variant === "primary-dark" &&
          "bg-primary-dark text-primary-foreground shadow-sm hover:bg-primary-dark/90",
        variant === "secondary" &&
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/85",
        variant === "outline" &&
          "bg-transparent text-foreground hover:bg-primary/5",
        variant === "ghost" &&
          "bg-transparent text-muted-foreground hover:bg-primary/5 hover:text-primary",
        variant === "destructive" &&
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        className
      )}
      data-slot="interactive-control"
      disabled={loading || props.disabled}
      {...props}
    >
      {content}
    </Comp>
  );
}
